import { useEffect, useMemo, useState } from 'react';
import { Loader2, Check, MapPin, CreditCard, Pause, Play, X, Calendar, Package, ArrowUpDown, FastForward, Target, Bell, Truck, Mail, AlertTriangle } from 'lucide-react';
import { loadHyper } from '@juspay-tech/hyper-js';
import { HyperElements, UnifiedCheckout } from '@juspay-tech/react-hyper-js';
import type { DtpProductResponse } from '@bloom/shared';
import { dtpApi } from '../../lib/dtpApi';
import { getCurrentTenantConfig } from '../../config/tenant';
import { wouldExceedWeightLossCap, WEIGHT_LOSS_CAP_MESSAGE } from '../../utils/membershipConstraints';
import { useTrackWhen } from '../analytics/useTrackWhen';

const TIERS: Array<{ tier: 1 | 2 | 3; productCount: 1 | 2 | 4; pricePerCycleCents: number }> = [
  { tier: 1, productCount: 1, pricePerCycleCents: 20000 },
  { tier: 2, productCount: 2, pricePerCycleCents: 30000 },
  { tier: 3, productCount: 4, pricePerCycleCents: 40000 }
];

type MembershipStatus = 'pending_clinical' | 'active' | 'paused' | 'past_due' | 'cancelled';

interface Props {
  membership: any;
  eligible: DtpProductResponse[];      // membership-eligible peptides at this clinic
  allProducts: DtpProductResponse[];   // for resolving family display names etc.
  onChange: (m: any) => void;          // bubble updated membership doc
  onCancelled: () => void;             // membership was cancelled — drop the panel
}

const STATUS_COPY: Record<MembershipStatus, { label: string; tone: string; explainer: string }> = {
  pending_clinical: {
    label: 'Awaiting card setup',
    tone: 'bg-amber-100 text-amber-800',
    explainer: 'Save a card on file to activate your membership and start the first cycle.'
  },
  active: {
    label: 'Active',
    tone: 'bg-green-100 text-green-800',
    explainer: 'Your membership is active and will renew automatically.'
  },
  paused: {
    label: 'Paused',
    tone: 'bg-gray-100 text-gray-700',
    explainer: 'No charges or shipments until you resume.'
  },
  past_due: {
    label: 'Payment failed',
    tone: 'bg-red-100 text-red-800',
    explainer: 'Your card was declined. Update your card below to retry — we\'ll automatically attempt the charge again. After 4 failed attempts the membership is cancelled.'
  },
  cancelled: {
    label: 'Cancelled',
    tone: 'bg-gray-100 text-gray-500',
    explainer: 'This membership is no longer active.'
  }
};

export function MembershipPortal({ membership, eligible, allProducts, onChange, onCancelled }: Props) {
  const status = (membership.status || 'active') as MembershipStatus;
  useTrackWhen(true, 'membership_viewed', { status });
  const meta = STATUS_COPY[status] || STATUS_COPY.active;
  const productCount: number = membership.productCount;
  const tier: number = membership.tier;
  const dollars = (membership.pricePerCycleCents / 100).toFixed(2);
  const nextBillingDate = membership.nextBillingDate ? new Date(membership.nextBillingDate) : null;

  // ---- Selection ----
  const currentSelectionIds = useMemo<string[]>(() => {
    return (membership.selectedProductIds || []).map((p: any) => typeof p === 'string' ? p : p._id || p.toString());
  }, [membership.selectedProductIds]);
  const pendingSelectionIds = useMemo<string[] | null>(() => {
    if (!membership.pendingSelectionChange?.productIds?.length) return null;
    return membership.pendingSelectionChange.productIds.map((p: any) => typeof p === 'string' ? p : p._id || p.toString());
  }, [membership.pendingSelectionChange]);

  const eligibleById = useMemo(() => new Map(eligible.map(p => [p._id, p])), [eligible]);
  const allProductsById = useMemo(() => new Map(allProducts.map(p => [p._id, p])), [allProducts]);

  function nameFor(id: string): string {
    return eligibleById.get(id)?.drugName
      || allProductsById.get(id)?.drugName
      || allProductsById.get(id)?.displayName
      || 'Product';
  }

  const [editingSelection, setEditingSelection] = useState(false);
  const [draftPicks, setDraftPicks] = useState<string[]>([]);
  const [selectionSaving, setSelectionSaving] = useState(false);
  const [selectionError, setSelectionError] = useState<string | null>(null);

  function startEditSelection() {
    setDraftPicks(pendingSelectionIds || currentSelectionIds);
    setEditingSelection(true);
    setSelectionError(null);
  }
  function toggleDraft(id: string) {
    setDraftPicks(prev => {
      if (prev.includes(id)) return prev.filter(x => x !== id);
      if (prev.length >= productCount) return prev;
      if (wouldExceedWeightLossCap(id, prev, eligible)) {
        setSelectionError(WEIGHT_LOSS_CAP_MESSAGE);
        return prev;
      }
      if (selectionError === WEIGHT_LOSS_CAP_MESSAGE) setSelectionError(null);
      return [...prev, id];
    });
  }
  async function saveSelection() {
    if (draftPicks.length !== productCount) {
      setSelectionError(`Pick exactly ${productCount} ${productCount === 1 ? 'peptide' : 'peptides'}.`);
      return;
    }
    setSelectionSaving(true);
    setSelectionError(null);
    try {
      const res: any = await dtpApi.swapMembershipSelection(draftPicks);
      if (!res.success) throw new Error(res.error || 'Could not save');
      onChange(res.membership);
      setEditingSelection(false);
    } catch (e: any) {
      setSelectionError(e?.response?.data?.error || e.message || 'Could not save');
    } finally {
      setSelectionSaving(false);
    }
  }

  // ---- Address + Email ----
  const [profileLoading, setProfileLoading] = useState(true);
  const [address, setAddress] = useState<any | null>(null);
  const [email, setEmail] = useState<string | undefined>(undefined);
  const [editingAddress, setEditingAddress] = useState(false);
  const [addressDraft, setAddressDraft] = useState({ street: '', street2: '', city: '', state: '', zipCode: '' });
  const [addressSaving, setAddressSaving] = useState(false);
  const [addressError, setAddressError] = useState<string | null>(null);
  const [editingEmail, setEditingEmail] = useState(false);
  const [emailDraft, setEmailDraft] = useState('');
  const [emailSaving, setEmailSaving] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setProfileLoading(true);
    dtpApi.getPatientProfile()
      .then((res: any) => {
        if (cancelled) return;
        if (res?.success) {
          setAddress(res.patient?.address || null);
          setEmail(res.patient?.email);
        }
      })
      .catch(() => { /* swallow — section just won't render */ })
      .finally(() => { if (!cancelled) setProfileLoading(false); });
    return () => { cancelled = true; };
  }, []);

  async function saveEmail() {
    const value = emailDraft.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      setEmailError('Enter a valid email address.');
      return;
    }
    setEmailSaving(true);
    setEmailError(null);
    try {
      const res: any = await dtpApi.updatePatientEmail(value);
      if (!res.success) throw new Error(res.error || 'Could not save');
      setEmail(res.email);
      setEditingEmail(false);
    } catch (e: any) {
      setEmailError(e?.response?.data?.error || e.message || 'Could not save');
    } finally {
      setEmailSaving(false);
    }
  }

  function startEditAddress() {
    setAddressDraft({
      street: address?.street || '',
      street2: address?.street2 || '',
      city: address?.city || '',
      state: address?.state || '',
      zipCode: address?.zipCode || ''
    });
    setEditingAddress(true);
    setAddressError(null);
  }
  async function saveAddress() {
    setAddressSaving(true);
    setAddressError(null);
    try {
      const res: any = await dtpApi.updatePatientAddress(addressDraft);
      if (!res.success) throw new Error(res.error || 'Could not save');
      setAddress(res.address);
      setEditingAddress(false);
    } catch (e: any) {
      setAddressError(e?.response?.data?.error || e.message || 'Could not save');
    } finally {
      setAddressSaving(false);
    }
  }

  // ---- Card replace ----
  const [replacingCard, setReplacingCard] = useState(false);
  const [cardClientSecret, setCardClientSecret] = useState<string | null>(null);
  const [hyperPromise, setHyperPromise] = useState<any>(null);
  const [cardError, setCardError] = useState<string | null>(null);

  async function startReplaceCard() {
    setReplacingCard(true);
    setCardError(null);
    try {
      const res: any = await dtpApi.startReplacePaymentMethod();
      if (!res.success || !res.clientSecret) throw new Error(res.error || 'Could not start card setup');
      setCardClientSecret(res.clientSecret);

      const cfg = getCurrentTenantConfig();
      const env = cfg.payments.hyperswitch.environment;
      const customBackendUrl = env === 'sandbox' ? 'https://sandbox.hyperswitch.io' : 'https://api.hyperswitch.io';
      setHyperPromise(loadHyper(cfg.payments.hyperswitch.publishableKey, { customBackendUrl }));
    } catch (e: any) {
      setCardError(e?.response?.data?.error || e.message || 'Could not start card setup');
      setReplacingCard(false);
    }
  }

  // ---- Pause / resume / cancel ----
  const [statusBusy, setStatusBusy] = useState(false);
  const [statusError, setStatusError] = useState<string | null>(null);

  async function pause() {
    setStatusBusy(true); setStatusError(null);
    try {
      const res: any = await dtpApi.pauseMembership();
      if (!res.success) throw new Error(res.error);
      onChange(res.membership);
    } catch (e: any) { setStatusError(e?.message || 'Pause failed'); }
    finally { setStatusBusy(false); }
  }
  async function resume() {
    setStatusBusy(true); setStatusError(null);
    try {
      const res: any = await dtpApi.resumeMembership();
      if (!res.success) throw new Error(res.error);
      onChange(res.membership);
    } catch (e: any) { setStatusError(e?.message || 'Resume failed'); }
    finally { setStatusBusy(false); }
  }
  async function cancel() {
    if (!confirm('Cancel this membership? Your current cycle will still ship.')) return;
    setStatusBusy(true); setStatusError(null);
    try {
      const res: any = await dtpApi.cancelMembership();
      if (!res.success) throw new Error(res.error);
      onCancelled();
    } catch (e: any) { setStatusError(e?.message || 'Cancel failed'); }
    finally { setStatusBusy(false); }
  }

  // ---- Tier change ----
  const [editingTier, setEditingTier] = useState(false);
  const [draftTier, setDraftTier] = useState<1 | 2 | 3>(membership.tier);
  const [tierPicks, setTierPicks] = useState<string[]>([]);
  const [tierSaving, setTierSaving] = useState(false);
  const [tierError, setTierError] = useState<string | null>(null);
  const draftTierConfig = TIERS.find(t => t.tier === draftTier)!;

  function startEditTier() {
    setDraftTier(membership.tier);
    setTierPicks(currentSelectionIds.slice(0, draftTierConfig.productCount));
    setEditingTier(true);
    setTierError(null);
  }
  function pickForTier(id: string) {
    setTierPicks(prev => {
      if (prev.includes(id)) return prev.filter(x => x !== id);
      if (prev.length >= draftTierConfig.productCount) return prev;
      if (wouldExceedWeightLossCap(id, prev, eligible)) {
        setTierError(WEIGHT_LOSS_CAP_MESSAGE);
        return prev;
      }
      if (tierError === WEIGHT_LOSS_CAP_MESSAGE) setTierError(null);
      return [...prev, id];
    });
  }
  async function saveTier() {
    if (draftTier === membership.tier) {
      setTierError('Pick a different tier or use Edit selection instead.');
      return;
    }
    if (tierPicks.length !== draftTierConfig.productCount) {
      setTierError(`Pick exactly ${draftTierConfig.productCount} ${draftTierConfig.productCount === 1 ? 'peptide' : 'peptides'}.`);
      return;
    }
    setTierSaving(true);
    setTierError(null);
    try {
      const res = await dtpApi.changeMembershipTier({ tier: draftTier, selectedProductIds: tierPicks });
      if (!res.success) throw new Error(res.error || 'Could not change tier');
      onChange(res.membership);
      setEditingTier(false);
    } catch (e: any) {
      setTierError(e?.response?.data?.error || e.message || 'Could not change tier');
    } finally {
      setTierSaving(false);
    }
  }

  // ---- Skip next cycle ----
  const [skipBusy, setSkipBusy] = useState(false);
  const [skipError, setSkipError] = useState<string | null>(null);
  async function skipNext() {
    if (!confirm('Skip the next cycle? Your billing date will move forward by one cycle.')) return;
    setSkipBusy(true); setSkipError(null);
    try {
      const res = await dtpApi.skipNextCycle();
      if (!res.success) throw new Error(res.error);
      onChange(res.membership);
    } catch (e: any) {
      setSkipError(e?.response?.data?.error || e.message || 'Skip failed');
    } finally {
      setSkipBusy(false);
    }
  }

  // ---- Goals ----
  // Mirror the options shown on the signup membership page (DtpMembership.tsx).
  // Keeping these in sync prevents patients from seeing raw keys in the read
  // view, or losing 'weight' / 'gh' selections silently when they edit goals.
  const GOAL_OPTIONS = [
    { key: 'weight', label: 'Weight management' },
    { key: 'gh', label: 'Growth hormone support' },
    { key: 'recovery', label: 'Recovery & repair' },
    { key: 'longevity', label: 'Longevity' },
    { key: 'metabolic', label: 'Energy & metabolism' },
    { key: 'collagen', label: 'Skin & collagen' }
  ];
  const [editingGoals, setEditingGoals] = useState(false);
  const [draftGoals, setDraftGoals] = useState<string[]>(membership.selectedGoals || []);
  const [goalsSaving, setGoalsSaving] = useState(false);
  function toggleGoal(k: string) {
    setDraftGoals(prev => prev.includes(k) ? prev.filter(g => g !== k) : [...prev, k]);
  }
  async function saveGoals() {
    setGoalsSaving(true);
    try {
      const res = await dtpApi.updateMembershipGoals(draftGoals);
      if (res.success) {
        onChange(res.membership);
        setEditingGoals(false);
      }
    } finally {
      setGoalsSaving(false);
    }
  }

  // ---- Notification preferences ----
  const prefs = membership.notificationPrefs || { sms: true, renewalReminder: true };
  const [prefsBusy, setPrefsBusy] = useState(false);
  async function setPref(key: 'sms' | 'renewalReminder', value: boolean) {
    setPrefsBusy(true);
    try {
      const res = await dtpApi.updateMembershipNotifications({ [key]: value });
      if (res.success) onChange(res.membership);
    } finally {
      setPrefsBusy(false);
    }
  }

  // ---- Cycle history (annotated with order/invoice) ----
  const [cycles, setCycles] = useState<any[]>(membership.cycleHistory || []);
  const [cyclesLoading, setCyclesLoading] = useState(false);
  useEffect(() => {
    let cancelled = false;
    setCyclesLoading(true);
    dtpApi.getMembershipCycles()
      .then(res => { if (!cancelled && res?.success) setCycles(res.cycles || []); })
      .catch(() => { /* keep raw cycles fallback */ })
      .finally(() => { if (!cancelled) setCyclesLoading(false); });
    return () => { cancelled = true; };
  }, []);

  // ---- Order detail modal ----
  const [openOrderId, setOpenOrderId] = useState<string | null>(null);
  const [orderDetail, setOrderDetail] = useState<{ order: any; invoice: any } | null>(null);
  const [orderLoading, setOrderLoading] = useState(false);
  useEffect(() => {
    if (!openOrderId) { setOrderDetail(null); return; }
    let cancelled = false;
    setOrderLoading(true);
    dtpApi.getMembershipOrder(openOrderId)
      .then(res => { if (!cancelled && res?.success) setOrderDetail({ order: res.order, invoice: res.invoice }); })
      .finally(() => { if (!cancelled) setOrderLoading(false); });
    return () => { cancelled = true; };
  }, [openOrderId]);

  // For a pending_clinical membership that never got a card attached (patient
  // bailed mid-vault, SDK failed, etc.), auto-open the card form so the resume
  // path is one click instead of "find the card section, scroll, click."
  useEffect(() => {
    if (
      status === 'pending_clinical' &&
      !membership.hasPaymentMethod &&
      !replacingCard &&
      !cardClientSecret &&
      !cardError
    ) {
      void startReplaceCard();
    }
    // We only want this to fire on initial mount for a pending_clinical row;
    // re-running it after the user explicitly cancels would be annoying.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="mt-10 space-y-6">
      {/* Pending-clinical resume banner — patient signed up but never saved
          a card. Surface it prominently so the next click finishes setup. */}
      {status === 'pending_clinical' && !membership.hasPaymentMethod && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 flex items-start gap-3">
          <div className="text-amber-700 mt-0.5">
            <AlertTriangle className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <div className="font-medium text-amber-900">Finish setting up your membership</div>
            <div className="text-sm text-amber-800 mt-1">
              Your selections are saved — we just need a card on file to start your first cycle.
            </div>
            {!replacingCard && (
              <button
                onClick={startReplaceCard}
                className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-md bg-amber-700 text-white text-sm font-medium hover:bg-amber-800"
              >
                <CreditCard className="w-4 h-4" /> Add a card
              </button>
            )}
            {cardError && <p className="mt-2 text-sm text-red-700">{cardError}</p>}
          </div>
        </div>
      )}

      {/* Past-due banner — call out failed first cycle prominently. */}
      {status === 'past_due' && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4 flex items-start gap-3">
          <div className="text-red-700 mt-0.5">
            <X className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <div className="font-medium text-red-900">Your last payment failed.</div>
            <div className="text-sm text-red-800 mt-1">
              {cycles.length === 0
                ? "We weren't able to charge your card for your first cycle. No peptides have shipped. Update your card below to retry."
                : "We weren't able to charge your card for your latest cycle. Update your card below — we'll automatically retry."}
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="portal-card p-6 md:p-8">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${meta.tone}`}>
              {meta.label}
            </span>
            <h2 className="mt-3 text-2xl font-semibold text-[var(--portal-ink)]">
              Tier {tier} — {productCount} {productCount === 1 ? 'peptide' : 'peptides'} per cycle
            </h2>
            <p className="text-[var(--portal-muted)] mt-1">${dollars} every {membership.cycleLengthDays || 45} days</p>
          </div>
          <div className="text-right">
            {nextBillingDate && status === 'active' && (
              <div className="text-xs uppercase tracking-wider text-[var(--portal-muted)]">Next charge</div>
            )}
            {nextBillingDate && status === 'active' && (
              <div className="text-sm text-[var(--portal-ink)] flex items-center gap-1.5 justify-end">
                <Calendar className="w-3.5 h-3.5" />
                {nextBillingDate.toLocaleDateString()}
              </div>
            )}
          </div>
        </div>
        <p className="mt-4 text-sm text-[var(--portal-muted)]">{meta.explainer}</p>

        {membership.skipNextCycle && status === 'active' && (
          <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-xs bg-blue-50 border border-blue-200 text-blue-800">
            <FastForward className="w-3.5 h-3.5" /> Next cycle skipped — billing resumes {nextBillingDate ? nextBillingDate.toLocaleDateString() : 'on the new schedule'}.
          </div>
        )}

        {/* Subscription controls */}
        <div className="mt-5 flex flex-wrap items-center gap-3 text-sm">
          {status === 'active' && !membership.skipNextCycle && (
            <button onClick={skipNext} disabled={skipBusy} className="inline-flex h-10 items-center gap-1.5 rounded-full bg-white/80 px-4 text-sm font-semibold text-[var(--portal-ink)] ring-1 ring-[var(--portal-line)] hover:bg-white disabled:opacity-50">
              <FastForward className="w-4 h-4" /> Skip next cycle
            </button>
          )}
          {status === 'active' && (
            <button onClick={pause} disabled={statusBusy} className="inline-flex h-10 items-center gap-1.5 rounded-full bg-white/80 px-4 text-sm font-semibold text-[var(--portal-ink)] ring-1 ring-[var(--portal-line)] hover:bg-white disabled:opacity-50">
              <Pause className="w-4 h-4" /> Pause
            </button>
          )}
          {status === 'paused' && (
            <button onClick={resume} disabled={statusBusy} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[var(--portal-primary)] text-[var(--portal-primary-fg)] font-semibold hover:opacity-90 disabled:opacity-50">
              <Play className="w-4 h-4" /> Resume
            </button>
          )}
          {status !== 'cancelled' && (
            <button onClick={cancel} disabled={statusBusy} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-red-700 hover:text-red-900 underline disabled:opacity-50">
              <X className="w-4 h-4" /> Cancel membership
            </button>
          )}
        </div>
        {statusError && <p className="mt-2 text-sm text-red-700">{statusError}</p>}
        {skipError && <p className="mt-2 text-sm text-red-700">{skipError}</p>}
      </div>

      {/* Tier change */}
      {status !== 'cancelled' && (
        <Section icon={<ArrowUpDown className="w-4 h-4" />} title="Tier">
          {!editingTier ? (
            <>
              <p className="text-sm text-[var(--portal-muted)]">
                Currently on tier {membership.tier} — {productCount} {productCount === 1 ? 'peptide' : 'peptides'} per cycle at ${dollars}.
              </p>
              <button onClick={startEditTier} className="mt-3 text-sm font-semibold text-[var(--portal-ink)] underline underline-offset-4">
                Change tier
              </button>
            </>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-4">
                {TIERS.map(t => {
                  const selected = draftTier === t.tier;
                  return (
                    <button
                      key={t.tier}
                      type="button"
                      onClick={() => { setDraftTier(t.tier); setTierPicks(prev => prev.slice(0, t.productCount)); }}
                      className={`text-left p-3 rounded-lg border text-sm transition ${
                        selected ? 'border-[var(--portal-primary)] bg-gray-50 text-[var(--portal-ink)]' : 'border-[var(--portal-line)] hover:border-gray-400'
                      }`}
                    >
                      <div className="font-medium">Tier {t.tier}</div>
                      <div className="text-xs text-[var(--portal-muted)]">
                        {t.productCount} {t.productCount === 1 ? 'peptide' : 'peptides'} · ${(t.pricePerCycleCents / 100).toFixed(0)}/cycle
                      </div>
                    </button>
                  );
                })}
              </div>
              <div className="text-xs text-[var(--portal-muted)] mb-2">
                Pick {draftTierConfig.productCount} peptides ({tierPicks.length}/{draftTierConfig.productCount}):
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {eligible.map(p => {
                  const selected = tierPicks.includes(p._id);
                  const atCap = !selected && tierPicks.length >= draftTierConfig.productCount;
                  const blockedByWeightLossCap =
                    !selected && wouldExceedWeightLossCap(p._id, tierPicks, eligible);
                  const disabled = atCap || blockedByWeightLossCap;
                  return (
                    <button
                      key={p._id}
                      type="button"
                      disabled={disabled}
                      title={blockedByWeightLossCap ? WEIGHT_LOSS_CAP_MESSAGE : undefined}
                      onClick={() => pickForTier(p._id)}
                      className={`text-left px-3 py-2 rounded-lg border text-sm transition ${
                        selected
                          ? 'border-[var(--portal-primary)] bg-gray-50'
                          : disabled
                            ? 'border-[var(--portal-line)] bg-[var(--store-bg,#f9fafb)] text-gray-400 cursor-not-allowed'
                            : 'border-[var(--portal-line)] hover:border-gray-400'
                      }`}
                    >
                      <span className="flex items-center gap-2">
                        {selected ? <Check className="w-4 h-4 text-[var(--portal-accent)]" /> : <span className="w-4 h-4 inline-block" />}
                        {p.drugName}
                      </span>
                    </button>
                  );
                })}
              </div>
              {tierError && <p className="mt-2 text-sm text-red-700">{tierError}</p>}
              <div className="mt-4 flex items-center gap-3">
                <button
                  onClick={saveTier}
                  disabled={tierSaving || draftTier === membership.tier || tierPicks.length !== draftTierConfig.productCount}
                  className="inline-flex h-10 items-center rounded-full bg-[var(--portal-primary)] px-4 text-sm font-semibold text-[var(--portal-primary-fg)] hover:opacity-90 disabled:opacity-50"
                >
                  {tierSaving ? 'Saving…' : 'Change tier'}
                </button>
                <button onClick={() => setEditingTier(false)} className="text-sm text-[var(--portal-muted)] hover:text-[var(--portal-muted)]">
                  Cancel
                </button>
              </div>
            </>
          )}
        </Section>
      )}

      {/* Selection */}
      <Section icon={<Package className="w-4 h-4" />} title="What you're getting next cycle">
        {pendingSelectionIds && (
          <div className="mb-3 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
            Change pending — applies on your next cycle.
          </div>
        )}
        {!editingSelection ? (
          <>
            <ul className="space-y-1.5">
              {(pendingSelectionIds || currentSelectionIds).map(id => (
                <li key={id} className="flex items-center gap-2 text-[var(--portal-ink)]">
                  <Check className="w-4 h-4 text-[var(--portal-accent)]" /> {nameFor(id)}
                </li>
              ))}
            </ul>
            {status !== 'cancelled' && eligible.length > 0 && (
              <button
                onClick={startEditSelection}
                className="mt-4 text-sm font-semibold text-[var(--portal-ink)] underline underline-offset-4"
              >
                Edit selection
              </button>
            )}
          </>
        ) : (
          <>
            <div className="text-xs text-[var(--portal-muted)] mb-2">
              Pick {productCount} of the available peptides ({draftPicks.length}/{productCount}):
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {eligible.map(p => {
                const selected = draftPicks.includes(p._id);
                const atCap = !selected && draftPicks.length >= productCount;
                const blockedByWeightLossCap =
                  !selected && wouldExceedWeightLossCap(p._id, draftPicks, eligible);
                const disabled = atCap || blockedByWeightLossCap;
                return (
                  <button
                    key={p._id}
                    type="button"
                    disabled={disabled}
                    title={blockedByWeightLossCap ? WEIGHT_LOSS_CAP_MESSAGE : undefined}
                    onClick={() => toggleDraft(p._id)}
                    className={`text-left px-3 py-2 rounded-lg border text-sm transition ${
                      selected
                        ? 'border-[var(--portal-primary)] bg-gray-50 text-[var(--portal-ink)]'
                        : disabled
                          ? 'border-[var(--portal-line)] bg-[var(--store-bg,#f9fafb)] text-gray-400 cursor-not-allowed'
                          : 'border-[var(--portal-line)] hover:border-gray-400'
                    }`}
                  >
                    <span className="flex items-center gap-2">
                      {selected ? <Check className="w-4 h-4 text-[var(--portal-accent)]" /> : <span className="w-4 h-4 inline-block" />}
                      {p.drugName}
                    </span>
                  </button>
                );
              })}
            </div>
            {selectionError && <p className="mt-2 text-sm text-red-700">{selectionError}</p>}
            <div className="mt-4 flex items-center gap-3">
              <button
                onClick={saveSelection}
                disabled={selectionSaving || draftPicks.length !== productCount}
                className="inline-flex h-10 items-center rounded-full bg-[var(--portal-primary)] px-4 text-sm font-semibold text-[var(--portal-primary-fg)] hover:opacity-90 disabled:opacity-50"
              >
                {selectionSaving ? 'Saving…' : 'Save selection'}
              </button>
              <button
                onClick={() => setEditingSelection(false)}
                className="text-sm text-[var(--portal-muted)] hover:text-[var(--portal-muted)]"
              >
                Cancel
              </button>
            </div>
          </>
        )}
      </Section>

      {/* Email */}
      <Section icon={<Mail className="w-4 h-4" />} title="Email">
        {profileLoading ? (
          <div className="flex items-center gap-2 text-sm text-[var(--portal-muted)]">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : !editingEmail ? (
          <>
            {email ? (
              <p className="text-sm text-[var(--portal-ink)]">{email}</p>
            ) : (
              <div className="flex items-start gap-2 text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <div>
                  <strong>No email on file.</strong> We can't send you receipts, renewal reminders, or payment-failure alerts without one. Add an email below.
                </div>
              </div>
            )}
            {status !== 'cancelled' && (
              <button
                onClick={() => { setEmailDraft(email || ''); setEditingEmail(true); setEmailError(null); }}
                className="mt-3 text-sm font-semibold text-[var(--portal-ink)] underline underline-offset-4"
              >
                {email ? 'Update email' : 'Add email'}
              </button>
            )}
          </>
        ) : (
          <div className="space-y-3">
            <input
              type="email"
              placeholder="you@example.com"
              value={emailDraft}
              onChange={e => setEmailDraft(e.target.value)}
              className="w-full px-3 py-2 border border-[var(--portal-line)] rounded-xl text-sm"
            />
            {emailError && <p className="text-sm text-red-700">{emailError}</p>}
            <div className="flex items-center gap-3">
              <button
                onClick={saveEmail}
                disabled={emailSaving}
                className="inline-flex h-10 items-center rounded-full bg-[var(--portal-primary)] px-4 text-sm font-semibold text-[var(--portal-primary-fg)] hover:opacity-90 disabled:opacity-50"
              >
                {emailSaving ? 'Saving…' : 'Save email'}
              </button>
              <button onClick={() => setEditingEmail(false)} className="text-sm text-[var(--portal-muted)] hover:text-[var(--portal-muted)]">
                Cancel
              </button>
            </div>
          </div>
        )}
      </Section>

      {/* Address */}
      <Section icon={<MapPin className="w-4 h-4" />} title="Shipping address">
        {profileLoading ? (
          <div className="text-sm text-[var(--portal-muted)] flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : !editingAddress ? (
          <>
            {address ? (
              <address className="not-italic text-sm text-[var(--portal-ink)] leading-relaxed">
                {address.street}{address.street2 ? `, ${address.street2}` : ''}<br />
                {address.city}, {address.state} {address.zipCode}
              </address>
            ) : (
              <p className="text-sm text-[var(--portal-muted)]">No address on file.</p>
            )}
            {status !== 'cancelled' && (
              <button onClick={startEditAddress} className="mt-3 text-sm font-semibold text-[var(--portal-ink)] underline underline-offset-4">
                Edit address
              </button>
            )}
          </>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <input
              placeholder="Street"
              value={addressDraft.street}
              onChange={e => setAddressDraft(d => ({ ...d, street: e.target.value }))}
              className="sm:col-span-2 px-3 py-2 border border-[var(--portal-line)] rounded-xl text-sm"
            />
            <input
              placeholder="Apt / Suite (optional, max 35 chars)"
              value={addressDraft.street2}
              onChange={e => setAddressDraft(d => ({ ...d, street2: e.target.value }))}
              maxLength={35}
              className="sm:col-span-2 px-3 py-2 border border-[var(--portal-line)] rounded-xl text-sm"
            />
            <input
              placeholder="City"
              value={addressDraft.city}
              onChange={e => setAddressDraft(d => ({ ...d, city: e.target.value }))}
              className="px-3 py-2 border border-[var(--portal-line)] rounded-xl text-sm"
            />
            <input
              placeholder="State (e.g. NY)"
              maxLength={2}
              value={addressDraft.state}
              onChange={e => setAddressDraft(d => ({ ...d, state: e.target.value.toUpperCase() }))}
              className="px-3 py-2 border border-[var(--portal-line)] rounded-md text-sm uppercase"
            />
            <input
              placeholder="ZIP"
              value={addressDraft.zipCode}
              onChange={e => setAddressDraft(d => ({ ...d, zipCode: e.target.value }))}
              className="px-3 py-2 border border-[var(--portal-line)] rounded-xl text-sm"
            />
            {addressError && <p className="sm:col-span-2 text-sm text-red-700">{addressError}</p>}
            <div className="sm:col-span-2 flex items-center gap-3">
              <button
                onClick={saveAddress}
                disabled={addressSaving}
                className="inline-flex h-10 items-center rounded-full bg-[var(--portal-primary)] px-4 text-sm font-semibold text-[var(--portal-primary-fg)] hover:opacity-90 disabled:opacity-50"
              >
                {addressSaving ? 'Saving…' : 'Save address'}
              </button>
              <button onClick={() => setEditingAddress(false)} className="text-sm text-[var(--portal-muted)] hover:text-[var(--portal-muted)]">
                Cancel
              </button>
            </div>
          </div>
        )}
      </Section>

      {/* Card */}
      <Section icon={<CreditCard className="w-4 h-4" />} title="Card on file">
        {!replacingCard ? (
          <>
            <p className="text-sm text-[var(--portal-muted)]">
              {membership.hasPaymentMethod ? 'A card is saved for recurring billing.' : 'No card on file yet.'}
            </p>
            {status !== 'cancelled' && (
              <button
                onClick={startReplaceCard}
                className="mt-3 text-sm font-semibold text-[var(--portal-ink)] underline underline-offset-4"
              >
                {membership.hasPaymentMethod ? 'Update card' : 'Add card'}
              </button>
            )}
            {cardError && <p className="mt-2 text-sm text-red-700">{cardError}</p>}
          </>
        ) : cardClientSecret && hyperPromise ? (
          <HyperElements
            hyper={hyperPromise}
            options={{
              publishableKey: getCurrentTenantConfig().payments.hyperswitch.publishableKey,
              clientSecret: cardClientSecret,
              loader: 'auto'
            }}
          >
            <UnifiedCheckout
              id="dtp-membership-replace-card"
              options={{
                layout: { type: 'tabs' },
                paymentMethodOrder: ['card'],
                sdkHandleConfirmPayment: {
                  handleConfirm: true,
                  buttonText: 'Save card',
                  confirmParams: { return_url: window.location.href }
                }
              } as any}
            />
          </HyperElements>
        ) : (
          <div className="text-sm text-[var(--portal-muted)] flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading payment form…
          </div>
        )}
      </Section>

      {/* Goals */}
      {status !== 'cancelled' && (
        <Section icon={<Target className="w-4 h-4" />} title="Your goals">
          {!editingGoals ? (
            <>
              {(membership.selectedGoals && membership.selectedGoals.length > 0) ? (
                <div className="flex flex-wrap gap-2">
                  {membership.selectedGoals.map((g: string) => {
                    const opt = GOAL_OPTIONS.find(o => o.key === g);
                    return (
                      <span key={g} className="inline-flex items-center px-3 py-1 rounded-full text-xs bg-gray-50 border border-[#c4d4c8] text-[var(--portal-ink)]">
                        {opt?.label || g}
                      </span>
                    );
                  })}
                </div>
              ) : (
                <p className="text-sm text-[var(--portal-muted)]">No goals saved.</p>
              )}
              <button onClick={() => { setDraftGoals(membership.selectedGoals || []); setEditingGoals(true); }} className="mt-3 text-sm font-semibold text-[var(--portal-ink)] underline underline-offset-4">
                Edit goals
              </button>
            </>
          ) : (
            <>
              <div className="flex flex-wrap gap-2">
                {GOAL_OPTIONS.map(opt => {
                  const active = draftGoals.includes(opt.key);
                  return (
                    <button
                      key={opt.key}
                      type="button"
                      onClick={() => toggleGoal(opt.key)}
                      className={`px-3 py-1.5 rounded-full text-sm border transition ${
                        active ? 'border-[var(--portal-primary)] bg-[var(--portal-primary)] text-[var(--portal-primary-fg)]' : 'border-[var(--portal-line)] bg-white text-[var(--portal-muted)] hover:border-gray-400'
                      }`}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
              <div className="mt-4 flex items-center gap-3">
                <button onClick={saveGoals} disabled={goalsSaving} className="inline-flex h-10 items-center rounded-full bg-[var(--portal-primary)] px-4 text-sm font-semibold text-[var(--portal-primary-fg)] hover:opacity-90 disabled:opacity-50">
                  {goalsSaving ? 'Saving…' : 'Save goals'}
                </button>
                <button onClick={() => setEditingGoals(false)} className="text-sm text-[var(--portal-muted)] hover:text-[var(--portal-muted)]">
                  Cancel
                </button>
              </div>
            </>
          )}
        </Section>
      )}

      {/* Notification preferences */}
      {status !== 'cancelled' && (
        <Section icon={<Bell className="w-4 h-4" />} title="Notifications">
          <div className="space-y-3 text-sm">
            <PrefRow
              label="Text messages (shipping updates)"
              value={prefs.sms !== false}
              busy={prefsBusy}
              onChange={v => setPref('sms', v)}
            />
            <PrefRow
              label="Email reminder 2 days before each renewal"
              value={prefs.renewalReminder !== false}
              busy={prefsBusy}
              onChange={v => setPref('renewalReminder', v)}
            />
          </div>
        </Section>
      )}

      {/* History */}
      {cyclesLoading ? (
        <Section title="Cycle history">
          <div className="flex items-center gap-2 text-sm text-[var(--portal-muted)]">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        </Section>
      ) : cycles.length > 0 && (
        <Section title="Cycle history">
          <ul className="divide-y divide-[var(--portal-line)]">
            {cycles.slice().reverse().map((c: any) => {
              const tracking = c.order?.trackingInfo?.trackingUrl || c.order?.trackingUrl;
              const trackingNumber = c.order?.trackingInfo?.trackingNumber;
              const orderId = c.order?._id;
              const orderStatus = c.order?.status;
              return (
                <li key={c.cycleNumber} className="py-3 text-sm">
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div>
                      <button
                        onClick={() => orderId && setOpenOrderId(orderId)}
                        disabled={!orderId}
                        className="text-[var(--portal-muted)] hover:text-[var(--portal-ink)] disabled:cursor-default disabled:hover:text-[var(--portal-muted)]"
                      >
                        Cycle {c.cycleNumber} · {new Date(c.startedAt).toLocaleDateString()}
                      </button>
                      {orderStatus && (
                        <span className="ml-2 inline-block px-2 py-0.5 rounded-full text-[11px] bg-gray-100 text-[var(--portal-muted)]">
                          {orderStatus}
                        </span>
                      )}
                    </div>
                    <span className={
                      c.status === 'paid' ? 'text-emerald-700' :
                      c.status === 'failed' ? 'text-red-700' : 'text-[var(--portal-muted)]'
                    }>
                      ${(c.amountChargedCents / 100).toFixed(0)} · {c.status}
                    </span>
                  </div>
                  {(tracking || trackingNumber) && (
                    <div className="mt-1 text-xs text-[var(--portal-ink)] flex items-center gap-1.5">
                      <Truck className="w-3.5 h-3.5" />
                      {tracking ? (
                        <a href={tracking} target="_blank" rel="noreferrer" className="underline">
                          {trackingNumber || 'Track shipment'}
                        </a>
                      ) : (
                        <span>{trackingNumber}</span>
                      )}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </Section>
      )}

      {/* Order detail modal */}
      {openOrderId && (
        <OrderDetailModal
          loading={orderLoading}
          order={orderDetail?.order}
          invoice={orderDetail?.invoice}
          onClose={() => setOpenOrderId(null)}
        />
      )}
    </div>
  );
}

function PrefRow({ label, value, busy, onChange }: { label: string; value: boolean; busy: boolean; onChange: (v: boolean) => void }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-[var(--portal-ink)]">{label}</span>
      <button
        type="button"
        role="switch"
        aria-checked={value}
        disabled={busy}
        onClick={() => onChange(!value)}
        className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-[var(--portal-accent)] focus:ring-offset-2 ${
          value ? 'bg-[var(--portal-accent)]' : 'bg-[rgba(36,26,21,0.15)]'
        } disabled:opacity-50`}
      >
        <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${value ? 'translate-x-5' : 'translate-x-0'}`} />
      </button>
    </div>
  );
}

function OrderDetailModal({ loading, order, invoice, onClose }: { loading: boolean; order: any; invoice: any; onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[var(--portal-card-solid)] rounded-[24px] max-w-lg w-full max-h-[90vh] overflow-y-auto p-6" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-[var(--portal-ink)]">Order detail</h3>
          <button onClick={onClose} className="text-[var(--portal-muted)] hover:text-[var(--portal-ink)]"><X className="w-5 h-5" /></button>
        </div>
        {loading ? (
          <div className="flex items-center justify-center py-12 text-[var(--portal-muted)]"><Loader2 className="w-5 h-5 animate-spin" /></div>
        ) : order ? (
          <div className="space-y-5 text-sm">
            <div>
              <div className="text-[11px] uppercase tracking-wider text-[var(--portal-muted)] mb-1">Order</div>
              <div className="text-[var(--portal-ink)]">#{order.orderNumber}</div>
              <div className="text-xs text-[var(--portal-muted)] mt-0.5">Status: {order.status}</div>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-wider text-[var(--portal-muted)] mb-1">Items</div>
              <ul className="divide-y divide-[var(--portal-line)]">
                {(order.items || []).map((it: any, idx: number) => (
                  <li key={idx} className="py-2 flex items-center justify-between">
                    <span>{it.productName} {it.quantity > 1 ? `× ${it.quantity}` : ''}</span>
                    <span>${(it.totalPrice ?? 0).toFixed(2)}</span>
                  </li>
                ))}
              </ul>
              <div className="border-t border-[var(--portal-line)] mt-2 pt-2 flex items-center justify-between font-medium">
                <span>Total</span>
                <span>${(order.totalAmount ?? 0).toFixed(2)}</span>
              </div>
            </div>
            {(order.trackingInfo?.trackingNumber || order.trackingUrl) && (
              <div>
                <div className="text-[11px] uppercase tracking-wider text-[var(--portal-muted)] mb-1">Tracking</div>
                {order.trackingInfo?.trackingUrl || order.trackingUrl ? (
                  <a href={order.trackingInfo?.trackingUrl || order.trackingUrl} target="_blank" rel="noreferrer" className="text-[var(--portal-ink)] underline">
                    {order.trackingInfo?.trackingNumber || 'Open tracking'}
                  </a>
                ) : (
                  <span>{order.trackingInfo?.trackingNumber}</span>
                )}
              </div>
            )}
            {invoice && (
              <div>
                <div className="text-[11px] uppercase tracking-wider text-[var(--portal-muted)] mb-1">Invoice</div>
                <div>#{invoice.invoiceNumber}</div>
                <div className="text-xs text-[var(--portal-muted)] mt-0.5">
                  Paid ${(invoice.amountPaid ?? 0).toFixed(2)} of ${(invoice.totalAmount ?? 0).toFixed(2)}
                </div>
              </div>
            )}
          </div>
        ) : (
          <p className="text-sm text-[var(--portal-muted)]">Could not load order.</p>
        )}
      </div>
    </div>
  );
}

function Section({ icon, title, children }: { icon?: React.ReactNode; title: string; children: React.ReactNode }) {
  return (
    <div className="portal-card p-6 sm:p-8">
      <div className="mb-4 flex items-center gap-3">
        {icon && <span aria-hidden="true" className="grid h-9 w-9 place-items-center rounded-xl bg-[var(--portal-tint-1)] text-[var(--portal-ink)]">{icon}</span>}
        <h3 className="portal-display text-lg font-semibold">{title}</h3>
      </div>
      {children}
    </div>
  );
}
